<template>
    <div class="mt-sm md:mt-lg">
        <products-added-to-cart v-show="productsAddedToCart">
            <template v-if="showInfoBoxForGanCustomer" slot="info-section">
                <div class="bg-neutral-lighter mt-xl flex flex-col md:flex-row justify-between items-center py-xl px-2xl rounded">
                    <div>
                        <h3 class="mb-sm">
                            {{ translation('broadband_configurator_tv_info_box_gan_headline') }}
                        </h3>
                        <p class="mb-0">
                            {{ translation('broadband_configurator_tv_info_box_gan_text') }}
                        </p>
                    </div>
                    <div>
                        <a :href="urlToTvBasePackages" class="btn btn-md is-ghost">
                            {{ translation('broadband_configurator_tv_info_box_gan_btn_text') }}
                        </a>
                    </div>
                </div>
            </template>
            <template v-else-if="showInfoBoxForRegularCustomer" slot="info-section">
                <div class="bg-neutral-lighter mt-xl flex flex-col md:flex-row justify-between items-center py-xl px-2xl rounded">
                    <div>
                        <h3 class="mb-sm">
                            {{ translation('broadband_configurator_tv_info_box_regular_headline') }}
                        </h3>
                        <p class="mb-0">
                            {{ translation('broadband_configurator_tv_info_box_regular_text') }}
                        </p>
                    </div>
                </div>
            </template>
            <template v-if="showLinkToTvBasePackages" slot="extra-button">
                <div class="mt-md mr-md md:mt-0">
                    <a :href="urlToTvBasePackages" class="btn btn-md is-ghost text-center">
                        {{ translation('broadband_configurator_tv_info_box_regular_btn_text') }}
                    </a>
                </div>
            </template>
        </products-added-to-cart>
        <transition name="cart">
            <div v-show="! productsAddedToCart" class="card has-footer">
                <h1 class="font-regular">{{ lang('Bredband','Broadband') }} {{ variantGroup.name }}</h1>

                <!-- eslint-disable vue/no-v-html -->
                <div
                    v-if="variantGroup.description"
                    class="text-md text-neutral-dark mb-lg md:mb-5xl lg:max-w-lg"
                    v-html="variantGroup.description"
                />

                <div v-if="campaigns.length > 0" class="md:flex md:mb-5xl">
                    <variant
                        v-for="campaign in campaigns"
                        :key="campaign.id"
                        :variant="campaign"
                        :is-campaign="true"
                        :selected-variant="selectedCampaign"
                        :product-type="lang('Kampanj','Campaign')"
                        :product-variation="campaign.name"
                        :price="getCampaignPrice(campaign)"
                        :price-after-campaign="getCampaignAfterPrice(campaign)"
                        @select="setCampaign"
                    />

                    <variant
                        v-if="! configurationData.replacesOriginal"
                        :is-campaign="false"
                        :variant="null"
                        :selected-variant="selectedCampaign"
                        :product-type="lang('Egna inställningar','Your settings')"
                        :product-variation="variantGroup.name"
                        :price="cheapestProductMonthlyPriceText"
                        @select="setCustomSettings"
                    />
                </div>

                <setting-with-text
                    :setting="variantGroup.name"
                    :title="lang('Hastighet ned','Download speed')"
                    :description="lang('Hur snabbt du kan ladda ned med din bredbandstjänst','How fast you can download with your broadband service')"
                />

                <setting-with-radio
                    v-if="! selectedCampaign"
                    v-model="selectedProduct"
                    :title="lang('Hastighet upp','Upload speed')"
                    :description="lang('Hur snabbt du kan ladda upp med din bredbandstjänst','How fast you can upload with your broadband service')"
                    :options="uploadSpeeds"
                />

                <setting-with-text
                    v-else
                    :setting="`${selectedCampaign.meta.maxUploadSpeed} Mbit/s`"
                    :title="lang('Hastighet upp','Upload speed')"
                    :description="lang('Hur snabbt du kan ladda upp med din bredbandstjänst','How fast you can upload with your broadband service')"
                />

                <setting-with-text
                    :setting="startFeeLabel"
                    :title="lang('Startavgift','Initial cost')"
                    :description="lang('Tjänstens startavgift','The service initial cost')"
                />

                <setting-with-text
                    :setting="bindingPeriodLabel"
                    :title="lang('Bindningstid','Binding period')"
                    :description="lang('Tjänstens bindningstid','The service binding period')"
                />

                <setting-with-text
                    :setting="terminationTimeLabel"
                    :title="lang('Uppsägningstid','Termination time')"
                    :description="lang('Tjänstens uppsägningstid','The service termination time')"
                />

                <router-selector
                    v-model="selectedRouter"
                    :routers="selectableRouters"
                    :configuration-data="configurationData"
                    :allow-none-selection="! selectedCampaignHasRouter"
                />

                <setting-with-radio
                    v-if="selectedRouter && selectedRouter.part_payments.length"
                    v-model="selectedPartPaymentPlanId"
                    :title="lang('Delbetalning','Part payment')"
                    :options="selectedRouter.part_payments"
                    :description="lang('Delbetala din router','Finance your router')"
                />

                <configuration-footer
                    :validates="validates"
                    :monthly-price="localMonthlyPrice"
                    :one-time-cost="oneTimeCost"
                    @add="addToCart"
                />
            </div>
        </transition>

        <products-added-to-cart v-show="addonsAddedToCart" :show-cart-button="false">
            {{ lang('Dina tillval är tillagda i varukorgen', 'Your addon products is added to the cart') }}
        </products-added-to-cart>
        <broadband-popular-addons
            v-show="productsAddedToCart && ! addonsAddedToCart"
            :available-extenders="availableExtenders"
            :available-security-packages="availableSecurityPackages"
            @completed="addonsAddedToCart = true"
        />

        <slot v-if="productsAddedToCart" />
    </div>
</template>
<script>
    import Variant from '../Variant';
    import SettingWithText from '../Settings/SettingWithText';
    import SettingWithRadio from '../Settings/SettingWithRadio';
    import ConfigurationFooter from '../ConfigurationFooter';
    import BaseConfigurator from '../BaseConfigurator';
    import ProductsAddedToCart from '../ProductsAddedToCart';
    import BroadbandPopularAddons from './BroadbandPopularAddons';
    import RouterSelector from '../Hardware/RouterSelector.vue';

    export default {
        components: {
            RouterSelector,
            Variant,
            SettingWithText,
            SettingWithRadio,
            ConfigurationFooter,
            ProductsAddedToCart,
            BroadbandPopularAddons,
        },
        extends: BaseConfigurator,
        props: {
            configurationData: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                variantGroup: this.configurationData.variantGroup,
                variants: this.configurationData.variants,
                campaigns: this.configurationData.campaigns,
                routers: this.configurationData.routers,
                hasCollectiveRouter: this.configurationData.hasCollectiveRouter,
                cheapestProductMonthlyPriceText: this.configurationData.cheapestProductMonthlyPriceText,
                selectedProduct: null,
                selectedCampaign: null,
                selectedRouter: null,
                productsAddedToCart: false,
                addonsAddedToCart: false,
                availableSecurityPackages: this.configurationData.securityPackages,
                availableExtenders: this.configurationData.extenders,
                urlToTvBasePackages: this.configurationData.urlToTvBasePackages,
                selectedPartPaymentPlanId: 0,
            };
        },
        computed: {
            selectedProductObject() {
                return Object.values(this.variants).find((variant) => {
                    return variant.article_id === this.selectedProduct;
                });
            },
            validates() {
                if (this.selectedCampaign) {
                    return true;
                }

                if (this.selectedProductObject && this.configurationData.hasRouter) {
                    return true;
                }

                if (this.selectedProductObject && this.selectableRouters.length === 0) {
                    return true;
                }

                if (this.selectedProductObject && this.selectableRouters.length > 0 && this.selectedRouter !== null) {
                    return true;
                }

                return false;
            },
            selectableRouters() {
                return this.selectedCampaignHasRouter ? this.selectedCampaign.routers : this.routers;
            },
            selectedCampaignHasRouter() {
                return this.selectedCampaign && this.selectedCampaign.routers.length > 0;
            },
            downSpeed() {
                return this.getAttributeFromSelected('down_speed');
            },
            upSpeed() {
                let speed = this.getAttributeFromSelected('up_speed');

                if (speed) {
                    return speed;
                }

                const upSpeeds = this.uploadSpeeds;
                if (upSpeeds && upSpeeds.length) {
                    speed = upSpeeds[0].value;
                }

                return speed;
            },
            uploadSpeeds() {
                return Object.keys(this.variants).map((key) => {
                    const variant = this.variants[key];
                    return {
                        value: variant.article_id,
                        name: 'variant_id',
                        placeholder: `${variant.meta.maxUploadSpeed} Mbit/s`,
                        price_label: variant.monthly_price_label,
                    };
                });
            },
            oneTimeCost() {
                let equipmentCost = 0;

                if (this.selectedRouter && ! this.selectedPartPaymentPlanId) {
                    equipmentCost = this.selectedRouter.price;
                }

                const totalCost = this.getAttributeFromSelected('start_fee', 0)
                    + equipmentCost;

                return format_sek(totalCost);
            },
            selectedPartPayment() {
                if (! this.selectedRouter || ! this.selectedPartPaymentPlanId || ! this.selectedRouter.hasOwnProperty('part_payments')) {
                    return null;
                }

                return this.selectedRouter.part_payments.find(partPayment => this.selectedPartPaymentPlanId === partPayment.value);
            },
            localMonthlyPrice() {
                // Override base method to take routers "part payment" into account
                let totalPrice = 0;
                let partPaymentPriceText = null;

                if (this.selectedRouter && this.selectedPartPayment) {
                    totalPrice += this.selectedPartPayment.price;
                    partPaymentPriceText = this.selectedPartPayment.price_label + ' i ' + this.selectedPartPayment.placeholder;
                }

                if (this.selectedProduct === null && this.selectedCampaign === null) {
                    return '-';
                }

                if (this.selectedCampaign !== null) {
                    totalPrice += this.getAttributeFromSelected('discounted_monthly_price');

                    return format_sek_per_month(totalPrice, partPaymentPriceText);
                }

                if (typeof this.getAttributeFromSelected('monthly_price') === 'number') {
                    totalPrice += this.getAttributeFromSelected('monthly_price');
                }

                if (totalPrice) {
                    return format_sek_per_month(totalPrice, partPaymentPriceText);
                }

                return '-';
            },
            showLinkToTvBasePackages() {
                return this.customerCanOrderTv
                    && ! this.isGanCustomer
                    && translation('broadband_configurator_tv_info_box_regular_btn_text');
            },
            customerCanOrderTv() {
              return this.configurationData.organizationTvBasePackages.length > 0;
            },
            showInfoBoxForRegularCustomer() {
                return this.customerCanOrderTv
                    && translation('broadband_configurator_tv_info_box_regular_headline')
                    && translation('broadband_configurator_tv_info_box_regular_text');
            },
            isGanCustomer() {
              return this.configurationData
                    .organizationTvBasePackages
                    .find((tvBasePackage) => tvBasePackage.is_collective);
            },
            showInfoBoxForGanCustomer() {
                return this.isGanCustomer
                    && translation('broadband_configurator_tv_info_box_gan_headline')
                    && translation('broadband_configurator_tv_info_box_gan_text')
                    && translation('broadband_configurator_tv_info_box_gan_btn_text');
            },
        },
        watch: {
            selectedCampaign(newCampaign, oldCampaign) {
                if (newCampaign === null) {
                    return;
                }

                if (newCampaign === oldCampaign) {
                    return;
                }

                this.selectedProduct = null;
            },
            selectedRouter(newRouter) {
                if (! newRouter || (newRouter.hasOwnProperty('part_payments') && newRouter.part_payments.length === 0)) {
                    this.selectedPartPaymentPlanId = 0;
                }
            },
        },
        created() {
            this.selectedProduct = this.upSpeed;
            this.trackProductDetailView();
        },
        methods: {
            setCustomSettings(variant) {
                this.selectedProduct = this.upSpeed;
                this.selectedCampaign = null;
            },
            setCampaign(campaign) {
                this.selectedCampaign = campaign;
            },
            async addToCart() {
                const articles = [];

                const mainProduct = {
                    articleClass: this.getAttributeFromSelected('article_class'),
                    articleId: this.getAttributeFromSelected('article_id'),
                    subItems: {},
                };

                if (this.selectedCampaignHasRouter && this.selectedRouter) {
                   mainProduct['subItems']['router'] = {
                       articleClass: this.selectedRouter.article_class,
                       articleId: this.selectedRouter.article_id,
                       parameters: this.selectedPartPayment ? {
                           PartPaymentId: this.selectedPartPayment.value,
                       } : {},
                   };
                } else if (this.selectedRouter) {
                    articles.push({
                        articleClass: this.selectedRouter.article_class,
                        articleId: this.selectedRouter.article_id,
                        parameters: this.selectedPartPayment ? {
                            PartPaymentId: this.selectedPartPayment.value,
                        } : {},
                    });
                }

                articles.push(mainProduct);

                const response = await axios.post(route('front.api.cart-items.store'), {
                    articles,
                });

                EventBus.$emit('cartDataUpdated', response.data);

                this.productsAddedToCart = true;
                this.scrollIt('#popular-addons');
                this.trackProductImpressions();
            },
            trackProductImpressions() {
                const products = [];

                this.availableSecurityPackages.forEach(securityPackage => products.push(securityPackage.gtm_product_data));
                this.availableExtenders.forEach(extender => products.push(extender.gtm_product_data));

                this.$GtmTracker.productImpressions(products, 'Populära tillval');
            },
            trackProductDetailView() {
                const products = [];

                this.campaigns.forEach(campaign => products.push(campaign.gtm_product_data));
                this.variants.forEach(variant => products.push(variant.gtm_product_data));

                this.$GtmTracker.productDetailView(products);
            },
        },
    };
</script>
